import ClearIcon from '@mui/icons-material/Clear'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import ListIcon from '@mui/icons-material/List'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import SearchIcon from '@mui/icons-material/Search'
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard'
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { UndoLastAction } from 'components/UndoLastAction'
import PoweredByGrayBook from 'components/PoweredByGrayBook'

const CONTROL_HEIGHT = 40

interface InventoryV3HeaderProps {
  inventoryCollection: 'master_inventory' | 'shield_inventory'
  isMobile: boolean
  canCreateInventory: boolean
  canModifyInventory: boolean
  view: 'list' | 'card'
  onViewChange: (_event: unknown, newView: 'list' | 'card' | null) => void
  onOpenAddVehicleForm: () => void
  searchDraft: string
  onSearchDraftChange: (value: string) => void
  onAddSearchTerms: () => void
  searchTerms: string[]
  onRemoveSearchTerm: (term: string) => void
  onClearSearchTerms: () => void
  opticOwnedOnly: boolean
  onOpticOwnedOnlyChange: (value: boolean) => void
  onClearFilters: () => void
  hasActiveFilters: boolean
  onZoomIn?: () => void
  onZoomOut?: () => void
  canZoomIn?: boolean
  canZoomOut?: boolean
}

export const InventoryV3Header = ({
  inventoryCollection,
  isMobile,
  canCreateInventory,
  canModifyInventory,
  view,
  onViewChange,
  onOpenAddVehicleForm,
  searchDraft,
  onSearchDraftChange,
  onAddSearchTerms,
  searchTerms,
  onRemoveSearchTerm,
  onClearSearchTerms,
  opticOwnedOnly,
  onOpticOwnedOnlyChange,
  onClearFilters,
  hasActiveFilters,
  onZoomIn,
  onZoomOut,
  canZoomIn = true,
  canZoomOut = true,
}: InventoryV3HeaderProps) => {
  const theme = useTheme()
  const title =
    inventoryCollection === 'master_inventory' ? 'Inventory' : 'Shield Inventory'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant='h6' sx={{ color: '#666666', fontSize: isMobile ? '1rem' : '1.1rem' }}>
          {title}
        </Typography>
        <PoweredByGrayBook size='small' />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          px: 2.5,
          py: 1.75,
          borderRadius: 2,
          backgroundColor: theme.palette.grey[200],
          border: '1px solid',
          borderColor: theme.palette.grey[300],
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size='small'
          value={searchDraft}
          onChange={event => onSearchDraftChange(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault()
              onAddSearchTerms()
            }
          }}
          placeholder='Search inventory — press Enter to add a term'
          sx={{
            flex: '1 1 220px',
            minWidth: 180,
            '& .MuiInputBase-root': { height: CONTROL_HEIGHT, backgroundColor: 'background.paper' },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {searchDraft ? (
                    <IconButton
                      size='small'
                      onClick={() => onSearchDraftChange('')}
                      aria-label='Clear search text'
                    >
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  ) : null}
                  <Tooltip title='Add search term'>
                    <span>
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={onAddSearchTerms}
                        disabled={!searchDraft.trim()}
                        aria-label='Add search term'
                      >
                        <SearchIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={opticOwnedOnly}
              onChange={event => onOpticOwnedOnlyChange(event.target.checked)}
              size='small'
            />
          }
          label='Optic'
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', mr: 0 }}
        />

        <Button
          variant='outlined'
          size='small'
          onClick={onClearFilters}
          startIcon={
            <Badge variant='dot' color='error' invisible={!hasActiveFilters}>
              <FilterAltOffIcon />
            </Badge>
          }
          sx={{
            textTransform: 'none',
            flexShrink: 0,
            height: CONTROL_HEIGHT,
            minHeight: CONTROL_HEIGHT,
            borderColor: hasActiveFilters ? theme.palette.error.main : 'divider',
            color: hasActiveFilters ? theme.palette.error.main : 'text.secondary',
          }}
        >
          Clear Filters
        </Button>

        <Button
          variant='contained'
          size='small'
          startIcon={<LocalShippingIcon />}
          onClick={onOpenAddVehicleForm}
          disabled={!canCreateInventory}
          sx={{
            textTransform: 'none',
            flexShrink: 0,
            height: CONTROL_HEIGHT,
            minHeight: CONTROL_HEIGHT,
            bgcolor: theme.palette.primary.main,
            '&:hover': { bgcolor: theme.palette.text.secondary },
          }}
        >
          Add Truck
        </Button>

        <Box
          sx={{
            flexShrink: 0,
            '& > .MuiButton-root': {
              ml: 0,
              height: CONTROL_HEIGHT,
              minHeight: CONTROL_HEIGHT,
            },
          }}
        >
          <UndoLastAction
            inventoryCollection={inventoryCollection}
            canModify={canModifyInventory || canCreateInventory}
            compact={isMobile}
          />
        </Box>

        {!isMobile ? (
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={onViewChange}
            size='small'
            sx={{
              flexShrink: 0,
              height: CONTROL_HEIGHT,
              '& .MuiToggleButton-root': {
                height: CONTROL_HEIGHT,
                px: 1.25,
              },
            }}
          >
            <ToggleButton value='list' aria-label='list view'>
              <ListIcon />
            </ToggleButton>
            <ToggleButton value='card' aria-label='card view'>
              <SpaceDashboardIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}

        {!isMobile && view === 'list' && onZoomIn && onZoomOut ? (
          <ButtonGroup
            size='small'
            variant='outlined'
            sx={{
              flexShrink: 0,
              height: CONTROL_HEIGHT,
              '& .MuiButton-root': {
                height: CONTROL_HEIGHT,
                minWidth: 40,
                px: 0,
                textTransform: 'none',
                fontSize: 24,
                fontWeight: 400,
                lineHeight: 1,
              },
            }}
          >
            <Tooltip title='Zoom out (Ctrl + scroll)'>
              <span>
                <Button onClick={onZoomOut} disabled={!canZoomOut} aria-label='Zoom out'>
                  −
                </Button>
              </span>
            </Tooltip>
            <Tooltip title='Zoom in (Ctrl + scroll)'>
              <span>
                <Button onClick={onZoomIn} disabled={!canZoomIn} aria-label='Zoom in'>
                  +
                </Button>
              </span>
            </Tooltip>
          </ButtonGroup>
        ) : null}
        </Box>

        {searchTerms.length > 0 || opticOwnedOnly ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {opticOwnedOnly ? (
              <Chip
                label='Optic Truck Works only'
                onDelete={() => onOpticOwnedOnlyChange(false)}
                color='primary'
                variant='outlined'
                size='small'
              />
            ) : null}
            {searchTerms.map((term, index) => (
              <Chip
                key={`${term}-${index}`}
                label={term}
                onDelete={() => onRemoveSearchTerm(term)}
                color='primary'
                variant='outlined'
                size='small'
              />
            ))}
            {searchTerms.length > 1 ? (
              <Chip
                label='Clear All'
                onClick={onClearSearchTerms}
                color='secondary'
                variant='outlined'
                size='small'
              />
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
